import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ExportControls = ({ userTier = 'free', onExport, onSave }) => {
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const tierPermissions = {
    free: {
      canExportPDF: false,
      canSave: false,
      canShare: false,
      message: "Upgrade to Pro to export PDF reports and save valuations"
    },
    pro: {
      canExportPDF: true,
      canSave: true,
      canShare: true,
      message: null
    },
    enterprise: {
      canExportPDF: true,
      canSave: true,
      canShare: true,
      message: null
    }
  };

  const permissions = tierPermissions?.[userTier] || tierPermissions?.free;

  const handleExportPDF = async () => {
    if (!permissions?.canExportPDF) {
      setShowEmailCapture(true);
      return;
    }

    setIsExporting(true);
    try {
      await onExport('pdf');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveValuation = async () => {
    if (!permissions?.canSave) {
      setShowEmailCapture(true);
      return;
    }

    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e?.preventDefault();
    if (email) {
      // Redirect to upgrade page with email pre-filled
      window.location.href = `/register?email=${encodeURIComponent(email)}&upgrade=pro`;
    }
  };

  const handleShare = () => {
    if (!permissions?.canShare) {
      setShowEmailCapture(true);
      return;
    }

    // Copy report URL to clipboard
    const reportUrl = window.location?.href;
    navigator.clipboard?.writeText(reportUrl)?.then(() => {
      // Show success message (could be implemented with toast)
      console.log('Report URL copied to clipboard');
    });
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="Download" size={20} />
          <span>Export & Share</span>
        </h2>
        {permissions?.message && (
          <p className="text-sm text-text-secondary mt-2">{permissions?.message}</p>
        )}
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* PDF Export */}
          <div className="space-y-3">
            <Button
              variant={permissions?.canExportPDF ? "default" : "outline"}
              onClick={handleExportPDF}
              loading={isExporting}
              iconName="FileText"
              iconPosition="left"
              fullWidth
              disabled={isExporting}
            >
              Export PDF
            </Button>
            <div className="text-xs text-text-secondary">
              Professional report with branding and legal disclaimers
            </div>
          </div>

          {/* Save Valuation */}
          <div className="space-y-3">
            <Button
              variant={permissions?.canSave ? "secondary" : "outline"}
              onClick={handleSaveValuation}
              loading={isSaving}
              iconName="Bookmark"
              iconPosition="left"
              fullWidth
              disabled={isSaving}
            >
              Save Report
            </Button>
            <div className="text-xs text-text-secondary">
              Save to your account for future reference
            </div>
          </div>

          {/* Share Report */}
          <div className="space-y-3">
            <Button
              variant={permissions?.canShare ? "secondary" : "outline"}
              onClick={handleShare}
              iconName="Share2"
              iconPosition="left"
              fullWidth
            >
              Share Link
            </Button>
            <div className="text-xs text-text-secondary">
              Generate secure shareable link
            </div>
          </div>

          {/* Print Report */}
          <div className="space-y-3">
            <Button
              variant="ghost"
              onClick={() => window.print()}
              iconName="Printer"
              iconPosition="left"
              fullWidth
            >
              Print
            </Button>
            <div className="text-xs text-text-secondary">
              Print current page view
            </div>
          </div>
        </div>

        {/* Report Details */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h3 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
            <Icon name="Info" size={16} />
            <span>Report Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-text-secondary mb-1">Report ID</div>
              <div className="text-text-primary font-mono">VR-{Date.now()?.toString()?.slice(-8)}</div>
            </div>
            <div>
              <div className="text-text-secondary mb-1">Generated</div>
              <div className="text-text-primary">
                {new Date()?.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <div>
              <div className="text-text-secondary mb-1">Valid Until</div>
              <div className="text-text-primary">
                {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US')}
              </div>
            </div>
            <div>
              <div className="text-text-secondary mb-1">Checksum</div>
              <div className="text-text-primary font-mono">SHA256-{Math.random()?.toString(36)?.substr(2, 8)}</div>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <h4 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span>Important Disclaimer</span>
          </h4>
          <p className="text-sm text-text-secondary leading-relaxed">
            This valuation report is provided for informational purposes only and should not be considered as a guarantee of actual market value. 
            Values may vary based on actual condition, market conditions, and other factors not reflected in this analysis. 
            CranePrice recommends professional inspection before making purchase decisions.
          </p>
        </div>
      </div>
      {/* Email Capture Modal */}
      {showEmailCapture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300">
          <div className="bg-surface border border-border rounded-lg shadow-modal max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">Upgrade Required</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowEmailCapture(false)}
                  iconName="X"
                />
              </div>
              
              <div className="text-center mb-6">
                <Icon name="Lock" size={48} className="text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary mb-4">
                  Upgrade to Pro to export PDF reports, save valuations, and access premium features.
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email to get started"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                />
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Upgrade to Pro - $999/month
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportControls;